"use client";

/**
 * FormModal
 * Reusable modal for displaying and submitting forms
 */

import GenericModal from "@/components/ui/modal/GenericModal";
import FormFieldRenderer from "@/features/forms/_components/renderer/FormFieldRenderer";
import { FieldComment } from "@/features/forms/_components/renderer/FieldComment";
import { FormSaveIndicator } from "@/features/forms/_components/shared/FormSaveIndicator";
import { useFormAutoSave } from "@/features/forms/_hooks/useFormAutoSave";
import { incrementFormViewCount } from "@/features/forms/_logic/form-server-actions";
import { isFormEffectivelyClosed, resolveClosedMessage } from "@/features/forms/_logic/form-status";
import { sanitizeFieldLabel } from "@/features/forms/_logic/sanitize-field-label";
import { getLegacyFormModalAccessAction } from "@/lib/access/respondent/legacy-modal-access-action";
import { getFormFields } from "@/lib/database/client/database";
import { FormField, commentKeyForLabel } from "@/lib/database/forms";
import { sendFormConfirmationEmail } from "@/lib/forms/send-confirmation-email";
import { useFormFileUpload } from "@/lib/forms/hooks";
import { initializeFormData, validateFormSubmission } from "@/lib/forms/utilities";
import { computeVisibleFields } from "@/lib/forms/conditional-visibility";
import { createClient } from "@/utils/supabase/client";
import { getSafeRedirectUrl } from "@/utils/url-utils";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLegacyLandingPageId } from "../../_hooks/useLegacyLandingPageId";
import type { FormStepLink } from "../../_logic/types";

// Memoized component to prevent re-renders when parent state changes. Renders a question label as
// sanitized HTML so its formatting shows and nothing in it runs.
const HtmlContent = memo(({ html, className }: { html: string; className?: string }) => (
  <span className={className} dangerouslySetInnerHTML={{ __html: sanitizeFieldLabel(html) }} />
));
HtmlContent.displayName = "HtmlContent";

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  formId: number;
  eventId: number;
  primaryColor: string;
  nextSteps?: FormStepLink[];
}

export default function FormModal({ isOpen, onClose, formId, eventId, primaryColor, nextSteps }: FormModalProps) {
  // Hooks
  const supabase = createClient();
  const { uploadFile, uploadingFields } = useFormFileUpload(supabase, formId);
  const landingPageId = useLegacyLandingPageId();

  // States
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [formData, setFormData] = useState<{ [key: string]: any }>({});
  const [formName, setFormName] = useState<string>("");
  const [formDescription, setFormDescription] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isFormLocked, setIsFormLocked] = useState(false);
  // Visitor-facing closed copy when the form no longer accepts responses; null while open.
  const [closedMessage, setClosedMessage] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [embeddedStepUrl, setEmbeddedStepUrl] = useState<string | null>(null);
  const [embeddedStepComplete, setEmbeddedStepComplete] = useState(false);
  const embeddedStepIframeRef = useRef<HTMLIFrameElement>(null);

  // Derived values
  const hasNextSteps = nextSteps && nextSteps.length > 0;
  const submitButtonText = hasNextSteps ? nextSteps[0].label || "Submit & Continue" : "Submit";

  const handleDraftRestored = useCallback((draftData: Record<string, unknown>) => {
    setFormData(draftData);
  }, []);

  const { isSaving, lastSaved, clearDraft, isAuthenticated } = useFormAutoSave({
    formId,
    eventId,
    formData,
    formFields,
    currentPage: 0,
    onDraftRestored: handleDraftRestored,
    disabled: isSubmitted || !isOpen || isLoading,
  });

  // Effects
  useEffect(() => {
    if (isOpen && formId) {
      loadForm();
    }
  }, [isOpen, formId]);

  // Listen for embedded step completion (e.g. hotel booking submitted)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only the embedded step's own iframe may report completion; any other window is ignored.
      if (event.source !== embeddedStepIframeRef.current?.contentWindow) return;
      if (event.data?.type === "hotel-booking-complete") {
        setEmbeddedStepComplete(true);
        setEmbeddedStepUrl(null);
        setCurrentStepIndex((prev) => prev + 1);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Handlers
  const loadForm = async () => {
    setIsLoading(true);
    setError(null);
    setClosedMessage(null);

    let isRedirecting = false;
    try {
      // The respondent decision, asked on the server (forms-respondent-identity). The browser still reads
      // the form to render it. `off` keeps today's path: only a public form renders.
      const accessResult = await getLegacyFormModalAccessAction({ formId, eventId, landingPageId });
      if (!accessResult.success) {
        setError(accessResult.error);
        return;
      }
      const { access } = accessResult;
      const signInUrl = access.kind === "needs_sign_in" ? getSafeRedirectUrl(access.redirectTo ?? undefined, "") : "";
      if (signInUrl) {
        // Keep the spinner up through the navigation.
        isRedirecting = true;
        window.location.assign(signInUrl);
        return;
      }
      if (access.kind === "needs_sign_in" || access.kind === "no_access") {
        setIsFormLocked(true);
        setFormName(access.kind === "no_access" ? (access.name ?? "") : "");
        return;
      }
      if (access.kind === "closed") {
        setFormName(access.name ?? "");
        setClosedMessage(access.closedMessage);
        return;
      }

      const form = await getFormFields(supabase, formId);
      if (form) {
        // Flag off: only a public form renders. On, the decision above already allowed this one.
        if (access.kind === "off" && !form.is_public) {
          setIsFormLocked(true);
          setFormName(form.name || "");
        } else if (isFormEffectivelyClosed(form)) {
          // Show the admin's closed copy instead of fields the server would reject on submit.
          setFormName(form.name || "");
          setClosedMessage(resolveClosedMessage(form.closed_message));
        } else {
          const fields = form.form_fields || [];
          setFormFields(fields);
          setFormName(form.name || "");
          setFormDescription(form.description || "");

          // Increment view count once per session (non-blocking, no auth required)
          const sessionKey = `form_viewed_${formId}`;
          if (typeof window !== "undefined" && !sessionStorage.getItem(sessionKey)) {
            sessionStorage.setItem(sessionKey, "true");
            incrementFormViewCount(formId).catch((error) => {
              console.error("Error updating view count:", error);
            });
          }

          // Initialize form data
          const initialData = initializeFormData(fields);
          setFormData(initialData);
        }
      } else {
        setError("Form not found.");
      }
    } catch (err) {
      console.error("Error fetching form:", err);
      setError("Failed to load form. Please try again.");
    } finally {
      if (!isRedirecting) setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormFields([]);
    setFormData({});
    setFormName("");
    setFormDescription("");
    setError(null);
    setIsSubmitted(false);
    setIsFormLocked(false);
    setClosedMessage(null);
    setCurrentStepIndex(0);
    setEmbeddedStepUrl(null);
    setEmbeddedStepComplete(false);
    onClose();
  };

  const handleChange = (label: string, value: string | string[]) => {
    setFormData((prevData) => ({
      ...prevData,
      [label]: value,
    }));
  };

  const handleFileUpload = async (field: FormField, file: File) => {
    const oldFilePath = formData[field.label];

    try {
      const filePath = await uploadFile(field, file, oldFilePath, null);

      if (filePath) {
        setFormData((prevData) => ({
          ...prevData,
          [field.label]: filePath,
        }));
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file. Please try again.");
    }
  };

  // Calculate visible fields based on conditional logic
  // This legacy (non-v2) landing page flow has no viewer/identity-resolution pipeline,
  // so respondent_type-gated show_if conditions never reveal anything here — only
  // answer-based conditions are functional in this renderer.
  const visibleFields = useMemo(() => computeVisibleFields(formFields, formData, null), [formFields, formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const validation = validateFormSubmission(visibleFields, formData);

    if (!validation.isValid) {
      alert("Please fill out all required fields.");
      setIsSubmitting(false);
      return;
    }

    // Clean up formData - null out any fields that are not visible (were skipped by conditional logic)
    const visibleFieldLabels = new Set(visibleFields.map((f) => f.label));
    const cleanedFormData = { ...formData };

    formFields.forEach((field) => {
      if (!visibleFieldLabels.has(field.label)) {
        // This field was skipped, set it to null
        cleanedFormData[field.label] = null;
      }
    });

    try {
      const response = await fetch("/api/forms/save-response", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formId,
          userId: null,
          formData: cleanedFormData,
          eventId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Failed to save form response" }));
        if (response.status === 410) {
          // The form closed after the modal loaded: swap to the closed state with the
          // server's copy (the admin's closed message, or the default).
          setClosedMessage(resolveClosedMessage(errorData.error));
          return;
        }
        throw new Error(errorData.error || "Failed to save form response");
      }

      const { success, responseId, confirmToken } = await response.json();

      if (success) {
        // Non-blocking: sendFormConfirmationEmail never throws, so this fires without holding
        // up the success transition below. The token proves this client just saved this response.
        sendFormConfirmationEmail({
          formId,
          responseId: responseId ?? null,
          confirmToken: confirmToken ?? null,
          source: "LandingPageFormModal",
        });

        await clearDraft();
        setIsSubmitted(true);
      } else {
        throw new Error("Failed to submit form");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render
  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleClose}
      title={embeddedStepUrl ? "" : !isLoading ? formName : ""}
      className={`w-[95vw] ${embeddedStepUrl ? "max-w-[850px]" : "max-w-[600px]"}`}
    >
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      )}

      {error && (
        <div className="text-center py-8">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {!isLoading && !error && !closedMessage && visibleFields.length > 0 && !isSubmitted && (
        <div>
          {formDescription && <p className="text-gray-600 mb-6 whitespace-pre-line text-left">{formDescription}</p>}

          <form
            className="space-y-4 [&_input]:text-base [&_select]:text-base [&_textarea]:text-base"
            onSubmit={handleSubmit}
          >
            {visibleFields.map((field, index) => (
              <div key={index} className="mb-4">
                <label className="block mb-2 font-medium text-gray-700 text-left">
                  <HtmlContent html={field.label} /> {field.is_required && <span className="text-red-500">*</span>}
                </label>
                <FormFieldRenderer
                  field={field}
                  value={formData[field.label]}
                  onChange={(value) => handleChange(field.label, value)}
                  onFileUpload={(file) => handleFileUpload(field, file)}
                  isUploading={uploadingFields[field.label]}
                  showValidationErrors={false}
                />
                <FieldComment
                  field={field}
                  value={formData[commentKeyForLabel(field.label)]}
                  onChange={(v) => handleChange(commentKeyForLabel(field.label), v)}
                />
              </div>
            ))}

            <div className="flex items-center justify-between gap-4">
              <FormSaveIndicator
                isSaving={isSaving}
                lastSaved={lastSaved}
                themeColor={primaryColor}
                isLocalOnly={!isAuthenticated}
                onClearDraft={clearDraft}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="shrink-0 touch-manipulation rounded-lg px-8 py-3 font-semibold text-white transition-all [@media(hover:hover)]:hover:opacity-90 disabled:opacity-50"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}DD 100%)`,
                }}
              >
                {isSubmitting ? "Submitting..." : submitButtonText}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Success Message */}
      {isSubmitted && !hasNextSteps && (
        <div className="text-center py-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank you!</h3>
          <p className="text-gray-600">Your response has been submitted successfully.</p>
        </div>
      )}

      {/* Next Steps — prompt to continue */}
      {isSubmitted && hasNextSteps && !embeddedStepUrl && !embeddedStepComplete && (
        <div className="text-center py-8">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <svg
              className="w-8 h-8"
              style={{ color: primaryColor }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Form submitted!</h3>
          <p className="text-gray-600 mb-6">Continue to the next step below.</p>

          {currentStepIndex < nextSteps!.length && (
            <div className="space-y-3">
              <button
                className="block w-full touch-manipulation rounded-lg px-8 py-3 font-semibold text-white transition-all [@media(hover:hover)]:hover:opacity-90"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}DD 100%)`,
                }}
                onClick={() => {
                  const step = nextSteps![currentStepIndex];
                  const separator = step.url.includes("?") ? "&" : "?";
                  setEmbeddedStepUrl(`${step.url}${separator}embedded=true`);
                }}
              >
                {nextSteps![currentStepIndex].label || "Continue"}
              </button>
              <button
                onClick={handleClose}
                className="mt-2 touch-manipulation text-sm text-gray-500 transition-colors [@media(hover:hover)]:hover:text-gray-700"
              >
                Skip for now
              </button>
            </div>
          )}
        </div>
      )}

      {/* Embedded step (e.g. hotel booking form inside modal) */}
      {embeddedStepUrl && (
        <div className="-mx-6 -mb-4">
          <iframe
            ref={embeddedStepIframeRef}
            src={embeddedStepUrl}
            className="w-full border-0 rounded-b-lg"
            style={{ minHeight: "70vh" }}
            title="Next Step"
          />
        </div>
      )}

      {/* All steps completed */}
      {isSubmitted && hasNextSteps && embeddedStepComplete && (
        <div className="text-center py-8">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <svg
              className="w-8 h-8"
              style={{ color: primaryColor }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">All done!</h3>
          <p className="text-gray-600 mb-6">Your form and booking have been submitted successfully.</p>
          <button
            onClick={handleClose}
            className="touch-manipulation rounded-lg px-8 py-3 font-semibold text-white transition-all [@media(hover:hover)]:hover:opacity-90"
            style={{
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}DD 100%)`,
            }}
          >
            Close
          </button>
        </div>
      )}

      {/* Locked Form Message */}
      {isFormLocked && (
        <div className="text-center py-8">
          <p className="text-gray-600">Form has not been unlocked yet.</p>
        </div>
      )}

      {closedMessage && (
        <div role="status" className="flex flex-col items-center gap-2 py-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900">Form closed</h3>
          <p className="max-w-prose whitespace-pre-line wrap-break-word text-sm font-medium text-gray-600">
            {closedMessage}
          </p>
        </div>
      )}
    </GenericModal>
  );
}
